"use client";

import { useEffect, useMemo, useState, Fragment } from "react";
import { DropListWithIconItem } from "@cede/types";
import { sortItemsByDescendingAmountAndAlphabetical, getCexLabel } from "@cede/utils";
import { Button, Input, PortalModal } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import Image from "next/image";

export type AccountSelectorItem = DropListWithIconItem<{
  cexValue?: string;
  isConnected: boolean;
  isSelected?: boolean;
  searchableTerms?: string[];
}>;

interface AccountSelectorProps {
  items: AccountSelectorItem[];
  value: AccountSelectorItem | null;
  onChange: (value: AccountSelectorItem | null) => void;
  shouldDisableItem?: (item: AccountSelectorItem) => boolean;
  onLinkAccount?: (cexValue: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  id?: string;
  disabled?: boolean;
}

const AccountSelectorTrigger = ({
  value,
  isOpen,
  onClick,
  disabled,
  id,
}: {
  value: AccountSelectorItem | null;
  disabled?: boolean;
  isOpen: boolean;
  onClick: () => void;
  id?: string;
}) => {
  // Get exchange name without numbers
  const displayLabel = value?.cexValue ? getCexLabel(value.cexValue as string) : value?.label;

  return (
    <button
      id={id}
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "flex h-[50px] w-full items-center justify-between gap-2.5 rounded-large border-[1.5px] border-cede-border-container bg-cede-bg-component px-3 py-2 outline-none transition-colors",
        "hover:bg-cede-primary/12",
        "focus-visible:ring-2 focus-visible:ring-cede-primary",
        "disabled:cursor-not-allowed disabled:opacity-50",
        isOpen && "border-cede-primary/30",
      )}
    >
      <div className="flex items-center gap-2.5">
        {value?.img && (
          <div className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-cede-bg-component">
            <Image
              src={value.img}
              alt={displayLabel || value.label}
              width={20}
              height={20}
              className="size-5 object-contain"
              unoptimized
              onError={(e) => {
                if (value.defaultImg) {
                  (e.target as HTMLImageElement).src = value.defaultImg;
                }
              }}
            />
          </div>
        )}
        <span
          className={cn(
            "font-inter text-[16px] font-medium leading-[20px]",
            displayLabel ? "text-cede-text-primary" : "text-cede-text-secondary",
          )}
        >
          {displayLabel || "Select"}
        </span>
      </div>
      <ChevronDown
        className={cn("size-3.5 shrink-0 text-cede-primary transition-transform duration-300", isOpen && "rotate-180")}
        strokeWidth={1.5}
      />
    </button>
  );
};

const AccountItem = ({
  item,
  onChange,
  shouldDisableItem,
  onClose,
  onLinkAccount,
}: {
  item: AccountSelectorItem;
  onChange: (value: AccountSelectorItem | null) => void;
  shouldDisableItem?: (item: AccountSelectorItem) => boolean;
  onClose: () => void;
  onLinkAccount?: (cexValue: string) => void;
}) => {
  const isDisabled = shouldDisableItem?.(item) || item.isDisabled;
  const isSelected = item.isSelected;

  const handleClick = () => {
    if (!isDisabled && item.isConnected) {
      onChange(item);
      onClose();
    }
  };

  // Get exchange name without numbers
  const displayLabel = item.cexValue ? getCexLabel(item.cexValue as string) : item.label;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled && item.isConnected}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition-colors min-h-[54px]",
        isSelected && "bg-cede-primary/10",
        !isSelected && !isDisabled && "hover:bg-cede-primary/5 focus:bg-cede-primary/5",
        isDisabled && item.isConnected && "cursor-not-allowed opacity-50",
        !isDisabled || !item.isConnected ? "cursor-pointer" : "",
      )}
    >
      {item.img && (
        <div className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-cede-bg-component">
          <Image
            src={item.img}
            alt={displayLabel}
            width={24}
            height={24}
            className="size-6 object-contain"
            unoptimized
            onError={(e) => {
              if (item.defaultImg) {
                (e.target as HTMLImageElement).src = item.defaultImg;
              }
            }}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col">
        <span className="font-inter text-[16px] font-medium leading-[20px] text-white">{displayLabel}</span>
        {item.isConnected && <span className="text-[12px] leading-[16px] text-cede-text-secondary">Linked</span>}
      </div>
      {!item.isConnected && onLinkAccount && (
        <Button
          variant="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onLinkAccount(item.cexValue ?? "");
            onClose();
          }}
        >
          Link
        </Button>
      )}
    </button>
  );
};

export const AccountSelector = ({
  items,
  value,
  onChange,
  shouldDisableItem,
  onLinkAccount,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  id,
}: AccountSelectorProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setIsOpen = (open: boolean) => {
    if (isControlled) {
      onOpenChange?.(open);
    } else {
      setUncontrolledOpen(open);
    }
  };

  const handleOpenRequest = (open: boolean) => {
    if (!open) {
      setIsOpen(false);
    } else if (!disabled) {
      setIsOpen(true);
    }
  };

  const filterItemList = (itemsToFilter: AccountSelectorItem[]): AccountSelectorItem[] => {
    const sortedItems = sortItemsByDescendingAmountAndAlphabetical<AccountSelectorItem>(
      itemsToFilter,
      shouldDisableItem as ((item: AccountSelectorItem) => boolean) | undefined,
    );

    const filteredAndSortedItems = !searchValue
      ? sortedItems
      : sortedItems.filter((item) => {
          const isLabelSearched = item.label.toLowerCase().includes(searchValue.toLowerCase());
          const searchableTerms = Array.isArray(item.searchableTerms) ? item.searchableTerms : [];
          const isSearchTermSearched = searchableTerms.some((searchableTerm) =>
            searchableTerm.toLowerCase().includes(searchValue.toLowerCase()),
          );
          return isLabelSearched || isSearchTermSearched;
        });

    // Move selected item to the beginning
    if (value?.value != null) {
      const selectedItemIndex = filteredAndSortedItems.findIndex((item) => item.value === value.value);
      if (selectedItemIndex > -1) {
        const selectedItem = filteredAndSortedItems.splice(selectedItemIndex, 1)[0];
        if (selectedItem) filteredAndSortedItems.unshift(selectedItem);
      }
    }

    return filteredAndSortedItems;
  };

  const allItems = useMemo(() => {
    return filterItemList(items);
  }, [items, searchValue, value?.value, shouldDisableItem]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
    }
  }, [isOpen]);

  return (
    <>
      <AccountSelectorTrigger
        value={value}
        isOpen={isOpen}
        disabled={disabled}
        onClick={() => handleOpenRequest(true)}
        id={id}
      />

      <PortalModal open={isOpen && !disabled} onOpenChange={handleOpenRequest} className="max-h-[80vh] pb-0">
        <PortalModal.Header>
          <PortalModal.Title>Select account</PortalModal.Title>
        </PortalModal.Header>

        <Input
          placeholder="Search..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="bg-cede-bg-component border-container text-cede-text-primary"
          containerClassName="pt-2 px-6"
          id="account-selector-search"
        />

        <PortalModal.Content scrollable className="pb-6">
          {allItems.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-cede-text-secondary">No results found</div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {allItems.map((item) => {
                const isSelected = value?.value === item.value;
                const itemWithSelected: AccountSelectorItem = { ...item, isSelected };
                return (
                  <Fragment key={item.value}>
                    <AccountItem
                      item={itemWithSelected}
                      onChange={onChange}
                      shouldDisableItem={shouldDisableItem}
                      onClose={() => handleOpenRequest(false)}
                      onLinkAccount={onLinkAccount}
                    />
                  </Fragment>
                );
              })}
            </div>
          )}
        </PortalModal.Content>
      </PortalModal>
    </>
  );
};
